/**
 * ImpactSummary — Executive-level mock KPIs shown above every demo tab.
 *
 * Every number here is derived live from the shared simulation state
 * so the viewer sees cause-and-effect across the three pages. We make
 * clear these are scenario estimates (banner inside DistributionDemoLayout
 * is enough — this component stays confident and clean).
 */
import React from 'react';
import { TrendingDown, ShieldCheck, Workflow, Boxes, Clock } from 'lucide-react';
import { useDistributionDemo } from '../DistributionDemoContext';

const formatEur = (n) => `€${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Metric = ({ icon: Icon, label, value, accent, hint, testid }) => (
    <div
        className="flex-1 min-w-[150px] rounded-lg border border-slate-200 bg-white px-4 py-3"
        data-testid={testid}
    >
        <div className="flex items-center gap-2 text-slate-500">
            <Icon className={`h-4 w-4 ${accent}`} />
            <span className="text-[11px] uppercase tracking-wider font-semibold">{label}</span>
        </div>
        <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{value}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
);

export default function ImpactSummary({ projections = [], impactAssumptions = {}, totalRevenueAtRisk = 0, stockoutsDetected = 0, idleAtSecondary = 0 }) {
    const { approvedCount, allocatedCount, resolvedInsights } = useDistributionDemo();

    const minutesSaved =
        (allocatedCount * (impactAssumptions.minutes_saved_per_allocation || 12)) +
        (approvedCount * (impactAssumptions.minutes_saved_per_po_approval || 35));

    // Revenue prevented by approved replenishments (coarse — assumes
    // every approved PO closes the gap it was raised for).
    const revenuePrevented = approvedCount > 0 ? totalRevenueAtRisk : 0;

    const resolvedInsightCount = Object.values(resolvedInsights).filter(Boolean).length;

    return (
        <div className="flex flex-wrap gap-3" data-testid="impact-summary">
            <Metric
                testid="impact-revenue-at-risk"
                icon={TrendingDown}
                label="Revenue at risk"
                value={formatEur(totalRevenueAtRisk)}
                accent="text-rose-600"
                hint={stockoutsDetected ? `${stockoutsDetected} SKU${stockoutsDetected === 1 ? '' : 's'} in supply gap` : 'All SKUs covered'}
            />
            <Metric
                testid="impact-revenue-prevented"
                icon={ShieldCheck}
                label="Prevented this session"
                value={formatEur(revenuePrevented)}
                accent="text-emerald-600"
                hint={approvedCount > 0 ? `${approvedCount} replenishment${approvedCount === 1 ? '' : 's'} approved` : 'Approve a PO to simulate'}
            />
            <Metric
                testid="impact-insights-resolved"
                icon={Workflow}
                label="Insights resolved"
                value={`${resolvedInsightCount}`}
                accent="text-indigo-600"
                hint="From the Command Center"
            />
            <Metric
                testid="impact-idle-inventory"
                icon={Boxes}
                label="Idle in secondary 3PL"
                value={formatEur(idleAtSecondary)}
                accent="text-amber-600"
                hint="Transfer-eligible"
            />
            <Metric
                testid="impact-time-saved"
                icon={Clock}
                label="Time saved"
                value={minutesSaved >= 60 ? `${(minutesSaved / 60).toFixed(1)} h` : `${minutesSaved} min`}
                accent="text-slate-600"
                hint="Automated decisions"
            />
        </div>
    );
}
